import { LinearIcon } from "@storytell/chat-ui/tools";
import { ProcessingSpinner } from "@storytell/chat-ui/ui";
import { type Component, For } from "solid-js";
import { useScene } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import {
  ApproveFoot,
  ChiefPane,
  DoneRow,
  NeedsCard,
  NeedsGroup,
  OfferFoot,
  PaneSection,
  PeopleStack,
  SessionPill,
  SessionWindow,
  startedLabel,
  Swap,
  type TranscriptEntry,
  TranscriptBlock,
} from "./internal/chief";
import { at, Cursor, MockScene, type Person } from "./internal/parts";
import { ScaledFrame } from "./ScaledFrame";

export type OfferFlowData = {
  project: string;
  session: string;
  started: string;
  clock: string;
  people: Person[];
  transcript: TranscriptEntry[];
  offer: { why: string };
  issue: { title: string; team: string; priority: string; ref: string };
};

// Seconds into the scene. The cursor glides for about 1.1s before it clicks.
const T = {
  offer: 1.0,
  acceptCursor: 1.7,
  accept: 2.8,
  held: 3.6,
  approveCursor: 4.3,
  approve: 5.4,
  filed: 5.9,
};

/**
 * The flagship sequence, played once: a line in the transcript asks for a
 * ticket, Chief's card for it lands under Needs you, Add in Linear is clicked,
 * the card waits for the OK, Approve files it, and it moves to Done.
 */
export const OfferFlowMockup: Component<{ data: OfferFlowData; class?: string }> = (props) => {
  const { t, currentLanguage } = useI18n();
  const scene = useScene();
  return (
    <MockScene scene={scene} label={t("marketing.mock.offer.alt")} class={props.class}>
      <ScaledFrame chrome="app" width={960} height={600} narrow={{ width: 400, height: 860 }}>
        <SessionWindow
          project={props.data.project}
          title={props.data.session}
          facts={`${t("marketing.mock.live")} · ${startedLabel(props.data.started, currentLanguage())}`}
          live
          tab="transcript"
          bar={
            <SessionPill clock={props.data.clock} people={<PeopleStack people={props.data.people} />} wave={false} />
          }
          pane={
            <ChiefPane class="h-110 shrink-0 @2xl:h-auto @2xl:w-xs">
              <OfferActivity data={props.data} />
            </ChiefPane>
          }
        >
          <div class="px-5 py-4">
            <For each={props.data.transcript}>
              {(entry, i) => (
                <TranscriptBlock
                  entry={entry}
                  class={i() < props.data.transcript.length - 2 ? "hidden @2xl:block" : undefined}
                />
              )}
            </For>
          </div>
        </SessionWindow>
      </ScaledFrame>
    </MockScene>
  );
};

/** Activity with the one offer: under Needs you while it waits and while it files, then under Done. */
const OfferActivity: Component<{ data: OfferFlowData }> = (props) => {
  const { t } = useI18n();
  return (
    <div class="grid p-1.5">
      <div class="exit col-start-1 row-start-1" style={at(T.filed)}>
        <PaneSection verb="waiting" count="1" />
        <NeedsGroup
          class="row-slide"
          style={at(T.offer)}
          icon={<LinearIcon class="size-3" />}
          label={t("marketing.mock.offer.group")}
          count={1}
        >
          <NeedsCard
            title={props.data.issue.title}
            meta={`${props.data.issue.team} · ${props.data.issue.priority}`}
            foot={
              <Swap
                swapAt={T.accept + 0.3}
                before={
                  <OfferFoot
                    icon={<LinearIcon class="size-3.5 shrink-0" />}
                    label={t("marketing.mock.offer.add")}
                    pressAt={T.accept}
                    cursor={<Cursor start={T.acceptCursor} />}
                  />
                }
                after={
                  <Swap
                    swapAt={T.held}
                    before={
                      <span class="flex h-6 items-center gap-1.5 text-xs text-brand">
                        <ProcessingSpinner class="size-2.5 border-brand border-t-transparent" />
                        {t("marketing.mock.offer.adding")}
                      </span>
                    }
                    after={<ApproveFoot pressAt={T.approve} cursor={<Cursor start={T.approveCursor} />} />}
                  />
                }
              />
            }
          >
            <p class="mt-1.5 text-xs leading-relaxed text-secondary-text">{props.data.offer.why}</p>
          </NeedsCard>
        </NeedsGroup>
      </div>
      <div class="card-appear col-start-1 row-start-1 self-start" style={at(T.filed)}>
        <PaneSection verb="did" count="1" />
        <DoneRow
          title={props.data.issue.title}
          where={`${t("marketing.mock.activity.filed_linear")} · ${props.data.issue.ref}`}
        />
      </div>
    </div>
  );
};
